import { useState } from "react";
import { Plus, Tag as TagIcon, Trash2, Archive, ArchiveRestore, Pencil } from "lucide-react";
import { api, ApiError } from "@/api/client";
import type { Tag } from "@/api/types";
import { useWorkspaceId, useSession } from "@/app/session";
import { useTags, invalidateWorkspace } from "@/features/data";
import { Page } from "@/components/Page";
import { Button, Card, EmptyState, ErrorState, IconButton, Input, Spinner } from "@/components/ui";
import { useToast, errorMessage } from "@/components/Toast";
import { cn } from "@/lib/cn";

export function TagsPage() {
  const wid = useWorkspaceId();
  const { workspace } = useSession();
  const isAdmin = workspace?.role !== "member";
  const { toast } = useToast();
  const { data: tags, isLoading, isError, refetch } = useTags(true);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  async function rename(t: Tag, next: string) {
    setEditingId(null);
    const trimmed = next.trim();
    if (!trimmed || trimmed === t.name) return;
    try {
      await api.patch(`/workspaces/${wid}/tags/${t.id}`, { name: trimmed });
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  async function create() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post(`/workspaces/${wid}/tags`, { name: name.trim() });
      setName("");
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function archive(t: Tag, archived: boolean) {
    try {
      await api.patch(`/workspaces/${wid}/tags/${t.id}`, { archived });
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  async function remove(t: Tag) {
    try {
      await api.del(`/workspaces/${wid}/tags/${t.id}`);
      invalidateWorkspace(wid);
    } catch (err) {
      toast(err instanceof ApiError && err.code === "in_use" ? "This tag is in use — archive it instead." : errorMessage(err), "error");
    }
  }

  return (
    <Page title="Tags" description="Flat, workspace-wide labels for time entries.">
      <div className="mb-5 flex max-w-sm gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="Add a tag…" />
        <Button size="sm" onClick={create} loading={busy} disabled={!name.trim()}><Plus className="size-4" /></Button>
      </div>
      {isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (tags?.length ?? 0) === 0 ? (
        <EmptyState icon={<TagIcon className="size-8" />} title="No tags yet" hint="Create tags to slice reports by cross-cutting themes." />
      ) : (
        <Card>
          {tags!.map((t, i) => (
            <div key={t.id} className={cn("flex items-center gap-3 px-4 py-2.5", i < tags!.length - 1 && "border-b border-line", t.archived && "opacity-60")}>
              <TagIcon className="size-3.5 text-faint" />
              {editingId === t.id ? (
                <input
                  autoFocus
                  defaultValue={t.name}
                  onBlur={(e) => rename(t, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                    if (e.key === "Escape") setEditingId(null);
                  }}
                  className="h-7 flex-1 rounded border border-accent bg-canvas px-2 text-sm text-ink focus:outline-none"
                />
              ) : (
                <span className="flex-1 text-sm text-ink">{t.name}</span>
              )}
              {isAdmin && (
                <>
                  <IconButton label="Rename" onClick={() => setEditingId(t.id)}><Pencil className="size-4" /></IconButton>
                  <IconButton label={t.archived ? "Unarchive" : "Archive"} onClick={() => archive(t, !t.archived)}>
                    {t.archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
                  </IconButton>
                  <IconButton label="Delete" onClick={() => remove(t)} className="hover:text-danger"><Trash2 className="size-4" /></IconButton>
                </>
              )}
            </div>
          ))}
        </Card>
      )}
    </Page>
  );
}
